/**
 * AdminNav component
 * Renders the role-aware admin navigation section with:
 * - CONFIGURE section header for global admin (small caps, refined typography)
 * - Flat list of configure items with icons in rounded backgrounds
 * - Selected state with left border accent
 * - "Command Docket" aesthetic - editorial legal meets mission control
 *
 * - Global admin: CONFIGURE header with flat list of children
 * - Org owner/admin: Direct "Admin" link to their organization
 * - Org user: Not shown
 */

import { Link, useLocation } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  mergeClasses,
  Tooltip,
  shorthands,
} from '@fluentui/react-components';
import { People24Regular, Building24Regular } from '@fluentui/react-icons';
import { useAuth } from '../../contexts/AuthContext';
import type { AdminNavItem } from './types';

// Brand blue for selected state border accent
const CONFIGURE_ACCENT_COLOR = '#0078D4';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  sectionHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '20px',
    paddingRight: '20px',
    marginBottom: '4px',
    height: '32px', // Fixed height to prevent any shift when collapsed
    boxSizing: 'border-box',
  },
  sectionHeaderRowCollapsed: {
    justifyContent: 'center',
    paddingLeft: '12px',
    paddingRight: '12px',
  },
  sectionHeader: {
    // Small caps typography - refined and editorial
    color: tokens.colorNeutralForeground3,
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    fontVariant: 'small-caps',
  },
  sectionHeaderCollapsed: {
    // Subtle dot separator when collapsed
    width: '4px',
    height: '4px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorNeutralStroke1,
  },
  itemList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    ...shorthands.padding('0', '12px'),
  },
  itemListCollapsed: {
    ...shorthands.padding('0', '8px'),
    alignItems: 'center',
  },
  item: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    ...shorthands.padding('6px', '10px'),
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
    ...shorthands.borderRadius('8px'),
    // Left border accent - hidden by default
    ...shorthands.borderLeft('3px', 'solid', 'transparent'),
    marginLeft: '-3px', // Compensate for border width
    transitionProperty: 'all',
    transitionDuration: '0.15s',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateX(2px)',
    },
  },
  itemCollapsed: {
    marginLeft: 0,
    ...shorthands.borderLeft('none'),
    ...shorthands.padding('6px'),
    justifyContent: 'center',
  },
  itemSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeftColor: CONFIGURE_ACCENT_COLOR,
  },
  itemIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    ...shorthands.borderRadius('8px'),
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    transitionProperty: 'all',
    transitionDuration: '0.15s',
  },
  itemIconSelected: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  itemLabel: {
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  itemLabelSelected: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  itemLabelCollapsed: {
    display: 'none',
  },
});

interface AdminNavProps {
  items: AdminNavItem[];
  collapsed?: boolean;
}

export function AdminNav({ items, collapsed = false }: AdminNavProps) {
  const styles = useStyles();
  const { user } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  // Don't render if no user
  if (!user) {
    return null;
  }

  // Determine behavior based on role
  const isGlobalAdmin = user.role === 'global_admin';
  const isOrgOwner = user.role === 'organization_owner';
  const isOrgAdmin = user.role === 'organization_admin';

  // Org users and managed tenant users don't see admin section
  if (!isGlobalAdmin && !isOrgOwner && !isOrgAdmin) {
    return null;
  }

  // For org owner/admin: CONFIGURE section with Users and Tenants links
  if (isOrgOwner || isOrgAdmin) {
    const orgItems = [
      { id: 'users', label: 'Users', route: 'admin/my-organization?tab=users', icon: <People24Regular /> },
      { id: 'tenants', label: 'Tenants', route: 'admin/my-organization?tab=tenants', icon: <Building24Regular /> },
    ];

    return (
      <div className={styles.section}>
        <div className={mergeClasses(
          styles.sectionHeaderRow,
          collapsed && styles.sectionHeaderRowCollapsed
        )}>
          <span className={collapsed ? styles.sectionHeaderCollapsed : styles.sectionHeader}>
            {!collapsed && 'CONFIGURE'}
          </span>
        </div>

        <div className={mergeClasses(
          styles.itemList,
          collapsed && styles.itemListCollapsed
        )}>
          {orgItems.map((item) => {
            const isSelected = currentPath.includes('/app/admin/my-organization') &&
              (currentPath.includes(`tab=${item.id}`) ||
               (item.id === 'users' && !currentPath.includes('tab=')));

            const itemContent = (
              <Link
                key={item.id}
                to={`/app/${item.route}`}
                className={mergeClasses(
                  styles.item,
                  isSelected && styles.itemSelected,
                  collapsed && styles.itemCollapsed
                )}
                data-testid={`configure-item-${item.id}`}
                data-selected={isSelected ? 'true' : undefined}
              >
                <span className={mergeClasses(
                  styles.itemIcon,
                  isSelected && styles.itemIconSelected
                )}>
                  {item.icon}
                </span>
                <span className={mergeClasses(
                  styles.itemLabel,
                  isSelected && styles.itemLabelSelected,
                  collapsed && styles.itemLabelCollapsed
                )}>
                  {item.label}
                </span>
              </Link>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.id} content={item.label} relationship="label" positioning="after">
                  {itemContent}
                </Tooltip>
              );
            }

            return itemContent;
          })}
        </div>
      </div>
    );
  }

  // For global admin: CONFIGURE section with flat list
  return (
    <div className={styles.section}>
      <div className={mergeClasses(
        styles.sectionHeaderRow,
        collapsed && styles.sectionHeaderRowCollapsed
      )}>
        <span className={collapsed ? styles.sectionHeaderCollapsed : styles.sectionHeader}>
          {!collapsed && 'CONFIGURE'}
        </span>
      </div>

      <div className={mergeClasses(
        styles.itemList,
        collapsed && styles.itemListCollapsed
      )}>
        {items.map((item) => {
          const isSelected = currentPath === `/app/${item.route}` || currentPath.startsWith(`/app/${item.route}/`);

          const itemContent = (
            <Link
              key={item.id}
              to={`/app/${item.route}`}
              className={mergeClasses(
                styles.item,
                isSelected && styles.itemSelected,
                collapsed && styles.itemCollapsed
              )}
              data-testid={`configure-item-${item.id}`}
              data-selected={isSelected ? 'true' : undefined}
            >
              <span className={mergeClasses(
                styles.itemIcon,
                isSelected && styles.itemIconSelected
              )}>
                {item.icon}
              </span>
              <span className={mergeClasses(
                styles.itemLabel,
                isSelected && styles.itemLabelSelected,
                collapsed && styles.itemLabelCollapsed
              )}>
                {item.label}
              </span>
            </Link>
          );

          // Wrap in tooltip when collapsed
          if (collapsed) {
            return (
              <Tooltip key={item.id} content={item.label} relationship="label" positioning="after">
                {itemContent}
              </Tooltip>
            );
          }

          return itemContent;
        })}
      </div>
    </div>
  );
}
